import { useEffect, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import Pagination from '../../Helper/Pagination';
import { deleteNotification, getNotificationsPaginated, updateSeenNotification } from "../../HTTP/user";
import { formatDateExtended, goToStaticPage } from "../../Helper/utils";
import { getStatusVisibleArticle } from "../../HTTP/articles";

export default function Notifications() {
  const url = new URL(window.location.href)
  if (url.searchParams.get('page') === null) {
    url.searchParams.set('page', 1);
    window.history.pushState({}, '', url);
  }

  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState(url.searchParams.get('query') === null ? '' : url.searchParams.get('query'));
  const [appliedQuery] = useState(url.searchParams.get('query') === null ? '' : url.searchParams.get('query'));
  const [currentPage, setCurrentPage] = useState(parseInt(url.searchParams.get('page')));
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadNotifications = async () => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage - 1;
    const resNotifs = await getNotificationsPaginated(start, end, appliedQuery);
    const notificationsRows = Array.isArray(resNotifs?.rows) ? resNotifs.rows : [];
    const computedTotal = Number.isInteger(resNotifs?.count) ? resNotifs.count : 0;
    
    // Traiter directement les notifications ici
    const updatedNotifications = await Promise.all(
      notificationsRows.map(async (notification) => {
        let visible_article = 0;
        
        if (notification.article_id !== null) {
          const res = await getStatusVisibleArticle(notification.article_id);
          visible_article = res ? res.visible : 0;
        }

        if (notification.seen === 0) {
          try {
            await updateSeenNotification(notification.id);
          } catch (error) {
            console.error("erreur pour mettre en vue les notifications", error);
          }
        }

        return {
          ...notification,
          visible_article,
          seen: 1
        };
      })
    );
    
     setNotifications(updatedNotifications);
     setSelectedNotifications([]);
     setTotalItems(computedTotal);
   };
  
  loadNotifications();
}, [currentPage, appliedQuery, itemsPerPage]); // Pas de dépendance
     
    const handleSelectNotification = (notificationId) => {
      setSelectedNotifications((prevSelected) =>
        prevSelected.includes(notificationId)
          ? prevSelected.filter((id) => id !== notificationId)
          : [...prevSelected, notificationId]
      );
    };

    const handleSelectAllNotifications = () => {
      if (notifications.length === 0) {
        return;
      }

      setSelectedNotifications((prevSelected) =>
        prevSelected.length === notifications.length
          ? []
          : notifications.map((notification) => notification.id)
      );
    };

    const handleDeleteSelectedNotifications = async () => {
      if (selectedNotifications.length === 0 || isDeleting) {
        return;
      }

      const confirmed = window.confirm("Are you sure you want to delete the selected notifications?");
      if (!confirmed) {
        return;
      }

      setIsDeleting(true);

      try {
        const deletedNotifications = await Promise.all(
          selectedNotifications.map((notificationId) => deleteNotification(notificationId))
        );

        const deletedIds = selectedNotifications.filter((_, index) => deletedNotifications[index]);

        if (deletedIds.length > 0) {
          setNotifications((prevNotifications) =>
            prevNotifications.filter((notification) => !deletedIds.includes(notification.id))
          );
          setSelectedNotifications([]);
          setTotalItems((prevTotalItems) => Math.max(prevTotalItems - deletedIds.length, 0));
        }
      } catch (error) {
        console.error("erreur lors de la suppression des notifications", error);
      } finally {
        setIsDeleting(false);
      }
    };

    const handleArticle = (articleId) => {
      window.location.href = "/admin/articles/check/" + articleId;
    }
    
    const handleReview = (reviewId) => {
      window.location.href = "/admin/reviews/check/" + reviewId;
    }

    const handleSearch = (e) => {
      e.preventDefault();
      window.location.href = "/notifications?page=1&query=" + encodeURIComponent(searchTerm);
    };

    const paginate = (pageNumber) => {
      setCurrentPage(pageNumber);
      url.searchParams.set('page', pageNumber);
      if (searchTerm && searchTerm.length > 0) {
        url.searchParams.set('query', searchTerm);
      } else {
        url.searchParams.delete('query');
      }
      window.history.pushState({}, '', url);
    };

    return (
        <>
        <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mt-10 mb-4">
        <h1 className="text-2xl font-bold text-center flex-grow">
          Mes notifications
        </h1>
      </div>
      <form onSubmit={handleSearch} className="w-full mb-4">
        <div className="flex flex-col space-y-3 lg:flex-row lg:space-y-0 lg:space-x-3">
          <div className="flex-1 flex space-x-2">
            <input
              type="text"
              placeholder="Rechercher..."
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 shadow-sm transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="submit"
              className="px-4 py-2.5 bg-zemusbis text-white rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              <SearchIcon />
            </button>
          </div>
        </div>
      </form>

      <div className="flex justify-end mb-4">
        <button
          type="button"
          className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleDeleteSelectedNotifications}
          disabled={selectedNotifications.length === 0 || isDeleting}
        >
          {isDeleting ? "Suppression..." : "Supprimer la sélection"}
        </button>
      </div>

      <div className="w-full h-[60vh] overflow-x-auto shadow-md rounded-lg">
        <table className="w-full table-auto">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={notifications.length > 0 && selectedNotifications.length === notifications.length}
                  onChange={handleSelectAllNotifications}
                  aria-label="Sélectionner toutes les notifications"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {notifications.map((item, index) => (
              <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-4 align-top">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.includes(item.id)}
                    onChange={() => handleSelectNotification(item.id)}
                    aria-label={`Sélectionner la notification ${item.id}`}
                  />
                </td>
                <td className="px-4 py-4 break-words">
                  <div className={item.seen === 0 ? " font-bold" : "font-normal max-w-xs lg:max-w-sm xl:max-w-md"}>{item.message}</div>
                </td>
                <td className="px-4 py-4 break-words">
                  <div className="max-w-xs lg:max-w-sm xl:max-w-md">{formatDateExtended(item.date)}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {
                    item.to_admin === 1 && item.visible_article === 1 && <button 
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
                    onClick={() => {handleArticle(item.article_id)
                    }}
                  >
                    Modérer l'article
                  </button>
                  }
                  {
                    item.article_id !== null && <button
                    className="bg-validbutton hover:bg-validbuttonhover text-white font-bold py-2 px-4 rounded mr-2"
                    onClick={() => goToStaticPage(true, item.article_id)}
                  >
                    Voir l'article
                  </button>
                  }
                  {
                    item.to_admin === 1 && item.review_id !== null && <button 
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
                    onClick={() => {handleReview(item.review_id)
                    }}
                  >
                    Modérer la revue
                  </button>
                  }
                  {
                    item.review_id !== null && <button
                    className="bg-validbutton hover:bg-validbuttonhover text-white font-bold py-2 px-4 rounded mr-2"
                    onClick={() => goToStaticPage(false, item.review_id)}
                  >
                    Voir la revue
                  </button>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        paginate={paginate}
        currentPage={currentPage}
      />
    </div>
        </>
    )
}
